import React, { useEffect, useState, useRef } from 'react';
import Header from '../../layouts/Header';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import ReusableTable from '../../components/ReusableTable';
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import { useSelector } from 'react-redux';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Syne:wght@600;700;800&display=swap');

  .ou-page-title {
    font-weight: 600;
    letter-spacing: -0.5px;
    color: #1a1a1a;
    margin: 0;
  }

  .ou-page-sub {
    font-size: 12px;
    color: #888;
    margin: 2px 0 0;
    letter-spacing: 0.3px;
  }

  /* ── OUTLET CARDS GRID ───────────────────────── */
  /*
   * The grid is wrapped in a container that also holds
   * the inline panel. We use a CSS grid with named areas
   * so the panel can be injected after the correct row.
   */
  .ou-cards-outer {
    margin-top: 20px;
  }

  .ou-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 14px;
  }

  /* Sentinel that sits right after the last card in the
     active row so we know where to inject the panel */
  .ou-row-panel-slot {
    grid-column: 1 / -1;   /* stretch full width */
    margin-top: 4px;
  }

  .ou-card {
    background: #fff;
    border-radius: 16px;
    padding: 20px 18px 16px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.22s cubic-bezier(.4,0,.2,1);
    position: relative;
    overflow: hidden;
  }

  .ou-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #2563eb, #7c3aed);
    opacity: 0;
    transition: opacity 0.22s;
  }

  .ou-card:hover {
    border-color: #e0e7ff;
    box-shadow: 0 8px 28px rgba(37,99,235,0.10);
    transform: translateY(-2px);
  }

  .ou-card:hover::before { opacity: 1; }

  .ou-card.active {
    border-color: #2563eb;
    box-shadow: 0 8px 28px rgba(37,99,235,0.18);
    transform: translateY(-2px);
  }

  .ou-card.active::before { opacity: 1; }

  .ou-card-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: linear-gradient(135deg, #eff6ff, #ede9fe);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 12px;
  }

  .ou-card-name {
    font-weight: 700;
    font-size: 14px;
    color: #1a1a1a;
    margin: 0 0 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ou-card-location {
    font-size: 11px;
    color: #999;
    margin: 0 0 12px;
  }

  .ou-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ou-status-pill {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4px;
    padding: 2px 8px;
    border-radius: 20px;
    text-transform: uppercase;
  }

  .ou-status-pill.active   { background: #dcfce7; color: #16a34a; }
  .ou-status-pill.inactive { background: #fee2e2; color: #dc2626; }
  .ou-status-pill.soon     { background: #fef3c7; color: #d97706; }

  .ou-card-arrow {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: #f0f4ff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #2563eb;
    font-size: 14px;
    transition: background 0.15s, transform 0.22s;
  }

  .ou-card.active .ou-card-arrow {
    background: #2563eb;
    color: #fff;
    transform: rotate(90deg);
  }

  /* ── INLINE USERS PANEL ──────────────────────── */
  .ou-panel {
    background: #fff;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    border: 1.5px solid #e8eeff;
    animation: slideDown 0.28s cubic-bezier(.4,0,.2,1);
  }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ou-panel-header {
    padding: 18px 22px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
  }

  .ou-panel-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ou-panel-badge {
    font-size: 11px;
    font-weight: 600;
    background: #eff6ff;
    color: #2563eb;
    border-radius: 20px;
    padding: 2px 10px;
  }

  .ou-search-wrap {
    position: relative;
  }

  .ou-search-wrap input {
    background: #f8f8f6;
    border: 1.5px solid #ebebeb;
    border-radius: 10px;
    padding: 6px 12px 6px 34px;
    font-size: 12px;
    color: #1a1a1a;
    outline: none;
    width: 220px;
    transition: border-color 0.15s;
  }

  .ou-search-wrap input:focus { border-color: #2563eb; }

  .ou-search-wrap i {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
    font-size: 14px;
  }

  .ou-add-btn {
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 7px 16px;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.15s;
  }

  .ou-add-btn:hover { opacity: 0.9; transform: translateY(-1px); }

  .ou-empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #aaa;
  }

  .ou-empty-state i { font-size: 48px; margin-bottom: 12px; display: block; opacity: 0.3; }
  .ou-empty-state p { font-size: 13px; margin: 0; }

  /* Table tweaks */
  .ou-panel .table { font-size: 12.5px; margin: 0; }
  .ou-panel .table th {
    background: #fafaf8;
    color: #888;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    border-bottom: 1px solid #f0f0f0;
    padding: 11px 16px;
  }
  .ou-panel .table td { padding: 12px 16px; vertical-align: middle; border-bottom: 1px solid #f8f8f8; }
  .ou-panel .table tbody tr:last-child td { border-bottom: none; }
  .ou-panel .table tbody tr:hover td { background: #fafbff; }

  /* Modals */
  .ou-modal .modal-content {
    border: none;
    border-radius: 18px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.14);
    font-size: 13px;
  }
  .ou-modal .modal-header {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px 22px;
    background: #fafaf8;
    border-radius: 18px 18px 0 0;
  }
  .ou-modal .modal-title {
    font-size: 15px;
    font-weight: 700;
  }
  .ou-modal .modal-footer {
    border-top: 1px solid #f0f0f0;
    background: #fafaf8;
    border-radius: 0 0 18px 18px;
    padding: 12px 22px;
  }
  .ou-modal .form-label { font-size: 11.5px; font-weight: 600; color: #555; margin-bottom: 4px; }
  .ou-modal .form-control,
  .ou-modal .form-select {
    font-size: 12.5px;
    border-radius: 9px;
    border: 1.5px solid #e8e8e8;
  }
  .ou-modal .form-control:focus,
  .ou-modal .form-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }

  .ou-btn-primary {
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    border: none;
    border-radius: 9px;
    font-size: 12px;
    font-weight: 600;
    padding: 7px 18px;
  }
  .ou-btn-secondary {
    background: #f4f3ef;
    border: none;
    color: #555;
    border-radius: 9px;
    font-size: 12px;
    font-weight: 600;
    padding: 7px 18px;
  }

  /* Loading skeleton */
  .ou-skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 8px;
    height: 14px;
  }

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// ── Inline Panel Component ────────────────────────────────────────────────────
const InlinePanel = ({
    selectedOutlet, filteredData, dataLoading, searchText, setSearchText,
    hasPermission, columns, getPaginatedData, page, setPage,
    rowsPerPage, setRowsPerPage, setInputData, setOpenAddModal, panelRef
}) => (
    <div className="ou-panel" ref={panelRef}>
        <div className="ou-panel-header">
            <h2 className="ou-panel-title">
                <i className="ri-team-line" style={{ color: "#2563eb" }}></i>
                {selectedOutlet.name}
                <span className="ou-panel-badge">{filteredData.length} users</span>
            </h2>
            <div className="d-flex align-items-center gap-2">
                <div className="ou-search-wrap">
                    <i className="ri-search-line"></i>
                    <input
                        type="text"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder="Search users..."
                    />
                </div>
                {hasPermission("create") && (
                    <button
                        className="ou-add-btn"
                        onClick={() => {
                            setInputData({ outlet: selectedOutlet._id, name: "", role: "", mobile: "", email: "", password: "" });
                            setOpenAddModal(true);
                        }}
                    >
                        <i className="ri-add-line"></i>Add User
                    </button>
                )}
            </div>
        </div>

        {dataLoading ? (
            <div style={{ padding: "40px 22px" }}>
                {[1, 2, 3].map(i => (
                    <div key={i} className="d-flex gap-3 mb-3">
                        <div className="ou-skeleton" style={{ width: 40, flexShrink: 0, borderRadius: 8 }}></div>
                        <div style={{ flex: 1 }}>
                            <div className="ou-skeleton" style={{ width: "60%", marginBottom: 8 }}></div>
                            <div className="ou-skeleton" style={{ width: "40%", height: 11 }}></div>
                        </div>
                    </div>
                ))}
            </div>
        ) : filteredData.length === 0 ? (
            <div className="ou-empty-state">
                <i className="ri-user-line"></i>
                <p>No users found for <strong>{selectedOutlet.name}</strong></p>
            </div>
        ) : hasPermission("get") ? (
            <ReusableTable
                columns={columns}
                data={getPaginatedData()}
                rowKey="serialNumber"
                loading={false}
                currentPage={page}
                recordsPerPage={rowsPerPage}
                onPageChange={setPage}
                totalPages={Math.ceil(filteredData?.length / rowsPerPage)}
                onRecordsPerPageChange={setRowsPerPage}
            />
        ) : null}
    </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const OutletUsers = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const { access, email } = useSelector((state) => state.login);
    const [outletAccess, setOutletAccess] = useState([]);
    const accessToken = localStorage.getItem("authToken");

    const [inputData, setInputData] = useState({ outlet: "", name: "", role: "", mobile: "", email: "", password: "" });
    const [outletUsers, setOutletUsers] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [searchText, setSearchText] = useState("");
    const [validationErrors, setValidationErrors] = useState({});
    const [editValidationErrors, setEditValidationErrors] = useState({});
    const [dataLoading, setDataLoading] = useState(false);
    const [outletsLoading, setOutletsLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditBtnLoading, setIsEditBtnLoading] = useState(false);
    const [isDeleteBtnLoading, setIsDeleteBtnLoading] = useState(false);
    const [outletUserId, setOutletUserId] = useState('');
    const [outletUserDetail, setOutletUserDetail] = useState({});
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // tracks which index in the outlets array was clicked
    const [activeIndex, setActiveIndex] = useState(null);
    // how many cards fit per row (computed from DOM)
    const [cardsPerRow, setCardsPerRow] = useState(5);

    const gridRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        if (Array.isArray(access)) {
            setOutletAccess(access?.filter(p => p?.startsWith("OutletUser:")));
        }
    }, [access]);

    const hasPermission = (action) =>
        outletAccess?.includes(`OutletUser:${action}`) || email === 'contact@xpansecafe.com';

    // ── Measure cards-per-row ─────────────────────────────────────────────────
    useEffect(() => {
        const measure = () => {
            if (!gridRef.current) return;
            const gridStyle = window.getComputedStyle(gridRef.current);
            const cols = gridStyle.gridTemplateColumns.split(' ').filter(Boolean).length;
            setCardsPerRow(cols || 1);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [outlets, outletsLoading]);

    // Re-measure whenever outlets load
    useEffect(() => {
        if (!outletsLoading && outlets.length > 0) {
            // small delay to let layout paint
            setTimeout(() => {
                if (!gridRef.current) return;
                const gridStyle = window.getComputedStyle(gridRef.current);
                const cols = gridStyle.gridTemplateColumns.split(' ').filter(Boolean).length;
                setCardsPerRow(cols || 1);
            }, 50);
        }
    }, [outletsLoading, outlets]);

    // Scroll panel into view after render
    useEffect(() => {
        if (panelRef.current) {
            panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [selectedOutlet]);

    // ── API ──────────────────────────────────────
    const getOutletUsers = async (outId) => {
        if (!outId) return;
        setDataLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(`outlet/user?outlet=${outId}`, { headers });
            if (response?.data?.status === true) {
                const users = response?.data?.data?.outletUsers.map((item, index) => ({
                    ...item, serialNumber: index + 1
                }));
                setOutletUsers(users);
                setFilteredData(users);
            }
        } catch (error) {
            if (error.response?.data?.status === false)
                toast.error(error.response.data.error);
        } finally {
            setDataLoading(false);
        }
    };

    const getSingleOutletUser = async (userId) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(`outlet/user?_id=${userId}`, { headers });
            if (response?.data?.status === true)
                setOutletUserDetail(response?.data?.data?.outletUsers);
        } catch (error) {
            if (error.response?.data?.status === false)
                toast.error(error.response.data.error);
        }
    };

    const getOutlets = async () => {
        setOutletsLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });
            if (response?.data?.status === true)
                setOutlets(response?.data?.data?.outlets);
        } catch (error) {
            if (error.response?.data?.status === false)
                toast.error(error.response.data.error);
        } finally {
            setOutletsLoading(false);
        }
    };

    // ── Handlers ──────────────────────────────────
    const handleCardClick = (outlet, index) => {
        // clicking active card collapses it
        if (selectedOutlet?._id === outlet._id) {
            setSelectedOutlet(null);
            setActiveIndex(null);
            return;
        }
        setSelectedOutlet(outlet);
        setActiveIndex(index);
        setSearchText("");
        setPage(1);
        getOutletUsers(outlet._id);
    };

    const validateForm = (data, isEdit = false) => {
        const errors = {};
        if (!data.outlet) errors.outlet = "Outlet is required";
        if (!data.name?.trim()) errors.name = "Name is required";
        if (!data.role) errors.role = "Role is required";
        if (!data.mobile) errors.mobile = "Mobile is required";
        if (data.mobile && data.mobile.length !== 10) errors.mobile = "Mobile must be 10 digits";
        if (data.mobile && !/^\d+$/u.test(data.mobile)) errors.mobile = "Invalid mobile number";
        if (!data.email?.trim()) errors.email = "Email is required";
        if (!isEdit && !data.password) errors.password = "Password is required";
        if (isEdit && !data.status) errors.status = "Status is required";
        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputData(prev => ({ ...prev, [name]: value }));
        setValidationErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm(inputData, false);
        if (Object.keys(errors).length > 0) { setValidationErrors(errors); return; }
        setIsLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.post("outlet/user", inputData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message);
                getOutletUsers(selectedOutlet?._id);
                setInputData({ outlet: "", name: "", role: "", mobile: "", email: "", password: "" });
                setOpenAddModal(false);
            }
        } catch (error) {
            toast.error(error?.response?.data?.error?.message || error?.response?.data?.error || "Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditModal = (data) => {
        setEditedData({ _id: data._id, outlet: data.outlet._id, name: data.name, role: data.role, mobile: data.mobile, email: data.email, status: data.status });
        setOutletUserId(data._id);
        setOpenEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm(editedData, true);
        if (Object.keys(errors).length > 0) { setEditValidationErrors(errors); return; }
        setIsEditBtnLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.put("outlet/user", editedData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message);
                getOutletUsers(selectedOutlet?._id);
                setOpenEditModal(false);
            }
        } catch (error) {
            toast.error(error?.response?.data?.error?.message || error?.response?.data?.error || "Something went wrong");
        } finally {
            setIsEditBtnLoading(false);
        }
    };

    const handleDeleteOutletUser = async () => {
        setIsDeleteBtnLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.delete(`outlet/user`, { data: { _id: outletUserId }, headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message);
                getOutletUsers(selectedOutlet?._id);
                setOpenDeleteModal(false);
            }
        } catch (error) {
            if (error.response?.data?.status === false)
                toast.error(error.response.data.error);
        } finally {
            setIsDeleteBtnLoading(false);
        }
    };

    useEffect(() => {
        if (!searchText.trim()) { setFilteredData(outletUsers); return; }
        const regex = new RegExp(searchText, "i");
        setFilteredData(outletUsers.filter(item =>
            item.name?.match(regex) || item.email?.match(regex) ||
            item.mobile?.match(regex) || item.role?.match(regex) || item.status?.match(regex)
        ));
        setPage(1);
    }, [searchText, outletUsers]);

    const getPaginatedData = () => {
        const start = (page - 1) * rowsPerPage;
        return filteredData?.slice(start, start + rowsPerPage);
    };

    useEffect(() => { getOutlets(); }, []);

    const roleOptions = [
        { value: "CAFE-MANAGER", label: "Cafe Manager" },
        { value: "MANAGER", label: "Manager" },
        { value: "CASHIER", label: "Cashier" }
    ];

    const statusOptions = [
        { value: "ACTIVE", label: "Active" },
        { value: "INACTIVE", label: "Inactive" }
    ];

    const getStatusClass = (status) => {
        if (status === "ACTIVE") return "active";
        if (status === "COMING_SOON") return "soon";
        return "inactive";
    };

    const columns = [
        { header: "Sr.", accessor: "serialNumber", width: "50px" },
        {
            header: "User",
            accessor: "name",
            render: (value, row) => (
                <div>
                    <div className="fw-medium" style={{ fontSize: "12.5px" }}>{value}</div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>{row.email}</div>
                </div>
            ),
        },
        {
            header: "Mobile",
            accessor: "mobile",
            render: (value) => <span style={{ fontSize: "12px", color: "#2563eb" }}>{value}</span>
        },
        {
            header: "Role",
            accessor: "role",
            render: (value) => (
                <span style={{ fontSize: "11px", fontWeight: 600, background: "#ede9fe", color: "#7c3aed", borderRadius: "20px", padding: "2px 10px" }}>
                    {value}
                </span>
            )
        },
        {
            header: "ID Number",
            accessor: "idNumber",
            render: (value) => <span style={{ fontSize: "12px", color: "#666" }}>{value || "—"}</span>
        },
        {
            header: "Status",
            accessor: "status",
            render: (value) => (
                <span className={`ou-status-pill ${getStatusClass(value)}`}>{value}</span>
            ),
        },
        {
            header: "Actions",
            accessor: null,
            width: "60px",
            render: (_, row) => (
                <div className="d-flex justify-content-center">
                    <div className="dropdown">
                        <button className="btn btn-link p-0 border-0 text-muted" data-bs-toggle="dropdown">
                            <i className="ri-more-2-fill" style={{ cursor: "pointer", fontSize: "16px" }}></i>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ fontSize: "12px" }}>
                            {hasPermission("update") && (
                                <li>
                                    <button className="dropdown-item" onClick={() => handleEditModal(row)}>
                                        <i className="ri-edit-line me-2 text-primary"></i>Edit
                                    </button>
                                </li>
                            )}
                            {hasPermission("get") && (
                                <li>
                                    <button className="dropdown-item" onClick={() => { getSingleOutletUser(row._id); setOpenViewModal(true); }}>
                                        <i className="ri-eye-line me-2 text-success"></i>View
                                    </button>
                                </li>
                            )}
                            {hasPermission("delete") && (
                                <li>
                                    <button className="dropdown-item" onClick={() => { setOpenDeleteModal(true); setOutletUserId(row._id); }}>
                                        <i className="ri-delete-bin-line me-2 text-danger"></i>Delete
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            ),
        },
    ];

    // ── Build interleaved grid items ──────────────────────────────────────────
    /**
     * Strategy:
     * 1. Render cards normally into the CSS grid.
     * 2. After the last card of the row that contains the active card,
     *    inject a full-width slot (grid-column: 1 / -1) with the panel.
     *
     * We know the active row ends at index:
     *   lastIndexInRow = min(activeIndex + (cardsPerRow - (activeIndex % cardsPerRow)), outlets.length) - 1
     */
    const buildGridItems = () => {
        if (activeIndex === null || !selectedOutlet) {
            // No selection — just render cards
            return outlets.map((outlet, i) => (
                <div
                    key={outlet._id}
                    className={`ou-card shadow-sm ${selectedOutlet?._id === outlet._id ? "active" : ""}`}
                    onClick={() => handleCardClick(outlet, i)}
                >
                    <div className="ou-card-icon">🏪</div>
                    <p className="ou-card-name" title={outlet.name}>{outlet.name}</p>
                    <p className="ou-card-location">{outlet.city || "—"}{outlet.state ? `, ${outlet.state}` : ""}</p>
                    <div className="ou-card-footer">
                        <span className={`ou-status-pill ${getStatusClass(outlet.status)}`}>{outlet.status}</span>
                        <div className="ou-card-arrow">
                            <i className="ri-arrow-right-line"></i>
                        </div>
                    </div>
                </div>
            ));
        }

        const col = activeIndex % cardsPerRow;           // 0-based column of active card
        const rowEnd = activeIndex + (cardsPerRow - col); // exclusive index of first card in next row
        const insertAfter = Math.min(rowEnd, outlets.length) - 1; // last card index in the active row

        const items = [];
        outlets.forEach((outlet, i) => {
            items.push(
                <div
                    key={outlet._id}
                    className={`ou-card shadow-sm ${selectedOutlet?._id === outlet._id ? "active" : ""}`}
                    onClick={() => handleCardClick(outlet, i)}
                >
                    <div className="ou-card-icon">🏪</div>
                    <p className="ou-card-name" title={outlet.name}>{outlet.name}</p>
                    <p className="ou-card-location">{outlet.city || "—"}{outlet.state ? `, ${outlet.state}` : ""}</p>
                    <div className="ou-card-footer">
                        <span className={`ou-status-pill ${getStatusClass(outlet.status)}`}>{outlet.status}</span>
                        <div className="ou-card-arrow">
                            <i className="ri-arrow-right-line"></i>
                        </div>
                    </div>
                </div>
            );

            // After the last card in the active row, inject the panel slot
            if (i === insertAfter) {
                items.push(
                    <div key="__panel_slot__" className="ou-row-panel-slot">
                        <InlinePanel
                            selectedOutlet={selectedOutlet}
                            filteredData={filteredData}
                            dataLoading={dataLoading}
                            searchText={searchText}
                            setSearchText={setSearchText}
                            hasPermission={hasPermission}
                            columns={columns}
                            getPaginatedData={getPaginatedData}
                            page={page}
                            setPage={setPage}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            setInputData={setInputData}
                            setOpenAddModal={setOpenAddModal}
                            panelRef={panelRef}
                        />
                    </div>
                );
            }
        });

        return items;
    };

    return (
        <React.Fragment>
            <style>{styles}</style>
            <Header onSkin={setSkin} />
            <div className="main main-app ou-root p-3 p-lg-4">

                {/* Page header */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <div>
                        <h3 className="ou-page-title">OUTLET USERS</h3>
                        <p className="ou-page-sub">Select an outlet to manage its staff</p>
                    </div>
                </div>

                {/* ── OUTLET CARDS ── */}
                <div className="ou-cards-outer">
                    {outletsLoading ? (
                        <div className="ou-cards-grid">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="ou-card" style={{ cursor: "default" }}>
                                    <div className="ou-skeleton" style={{ width: 42, height: 42, borderRadius: 12, marginBottom: 12 }}></div>
                                    <div className="ou-skeleton" style={{ width: "70%", marginBottom: 8 }}></div>
                                    <div className="ou-skeleton" style={{ width: "50%", height: 11 }}></div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="ou-cards-grid" ref={gridRef}>
                            {buildGridItems()}
                        </div>
                    )}
                </div>

                {!selectedOutlet && !outletsLoading && outlets.length > 0 && (
                    <div style={{ textAlign: "center", marginTop: 48, color: "#bbb" }}>
                        <i className="ri-store-2-line" style={{ fontSize: 52, display: "block", marginBottom: 10, opacity: 0.25 }}></i>
                        <p style={{ fontSize: 13 }}>Click on an outlet card to view its users</p>
                    </div>
                )}

                {/* ── ADD MODAL ── */}
                <Modal dialogClassName="ou-modal" show={openAddModal} onHide={() => setOpenAddModal(false)} centered size="md">
                    <Modal.Header closeButton>
                        <Modal.Title>Add New User — {selectedOutlet?.name}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body className="py-3 px-4">
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Outlet <span className="text-danger">*</span></Form.Label>
                                        <Form.Select name="outlet" value={inputData.outlet} onChange={handleChange} size="sm">
                                            <option value="">Select Outlet</option>
                                            {outlets.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
                                        </Form.Select>
                                        {validationErrors.outlet && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{validationErrors.outlet}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                                        <Form.Select name="role" onChange={handleChange} size="sm">
                                            <option value="">Select Role</option>
                                            {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                        </Form.Select>
                                        {validationErrors.role && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{validationErrors.role}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="text" name="name" value={inputData.name} onChange={handleChange} size="sm" />
                                        {validationErrors.name && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{validationErrors.name}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="email" name="email" value={inputData.email} onChange={handleChange} size="sm" />
                                        {validationErrors.email && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{validationErrors.email}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Mobile <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="tel" name="mobile" value={inputData.mobile} onChange={handleChange} size="sm" maxLength={10} />
                                        {validationErrors.mobile && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{validationErrors.mobile}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="password" name="password" value={inputData.password} onChange={handleChange} size="sm" />
                                        {validationErrors.password && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{validationErrors.password}</div>}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="ou-btn-secondary" onClick={() => setOpenAddModal(false)}>Cancel</button>
                            <button type="submit" className="ou-btn-primary" disabled={isLoading}>
                                {isLoading ? <Spinner animation="border" size="sm" /> : "Add User"}
                            </button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* ── EDIT MODAL ── */}
                <Modal dialogClassName="ou-modal" show={openEditModal} onHide={() => setOpenEditModal(false)} centered size="md">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Outlet User</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleEditSubmit}>
                        <Modal.Body className="py-3 px-4">
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Outlet <span className="text-danger">*</span></Form.Label>
                                        <Form.Select name="outlet" value={editedData.outlet} onChange={e => setEditedData(p => ({ ...p, outlet: e.target.value }))} size="sm">
                                            <option value="">Select Outlet</option>
                                            {outlets.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
                                        </Form.Select>
                                        {editValidationErrors.outlet && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{editValidationErrors.outlet}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                                        <Form.Select name="role" value={editedData.role} onChange={e => setEditedData(p => ({ ...p, role: e.target.value }))} size="sm">
                                            <option value="">Select Role</option>
                                            {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                        </Form.Select>
                                        {editValidationErrors.role && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{editValidationErrors.role}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="text" name="name" value={editedData.name} onChange={e => setEditedData(p => ({ ...p, name: e.target.value }))} size="sm" />
                                        {editValidationErrors.name && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{editValidationErrors.name}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="email" name="email" value={editedData.email} onChange={e => setEditedData(p => ({ ...p, email: e.target.value }))} size="sm" />
                                        {editValidationErrors.email && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{editValidationErrors.email}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Mobile <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="tel" name="mobile" value={editedData.mobile} onChange={e => setEditedData(p => ({ ...p, mobile: e.target.value }))} size="sm" maxLength={10} />
                                        {editValidationErrors.mobile && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{editValidationErrors.mobile}</div>}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                                        <Form.Select name="status" value={editedData.status} onChange={e => setEditedData(p => ({ ...p, status: e.target.value }))} size="sm">
                                            <option value="">Select Status</option>
                                            {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </Form.Select>
                                        {editValidationErrors.status && <div className="text-danger" style={{ fontSize: 11, marginTop: 3 }}>{editValidationErrors.status}</div>}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="ou-btn-secondary" onClick={() => setOpenEditModal(false)}>Cancel</button>
                            <button type="submit" className="ou-btn-primary" disabled={isEditBtnLoading}>
                                {isEditBtnLoading ? "Saving..." : "Save Changes"}
                            </button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* ── VIEW MODAL ── */}
                <Modal dialogClassName="ou-modal" show={openViewModal} onHide={() => setOpenViewModal(false)} centered size="sm">
                    <Modal.Header closeButton>
                        <Modal.Title>User Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="py-3 px-4">
                        <div style={{ background: "#f8f8f6", borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: 1, textTransform: "uppercase", margin: "0 0 10px" }}>Personal Info</p>
                            {[
                                ["ID Number", outletUserDetail?.idNumber],
                                ["Name", outletUserDetail?.name],
                                ["Email", outletUserDetail?.email],
                                ["Role", outletUserDetail?.role],
                                ["Mobile", outletUserDetail?.mobile],
                            ].map(([label, val]) => (
                                <div key={label} className="d-flex justify-content-between mb-2" style={{ fontSize: 12 }}>
                                    <span style={{ color: "#888" }}>{label}</span>
                                    <span style={{ fontWeight: 500 }}>{val || "N/A"}</span>
                                </div>
                            ))}
                            <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                                <span style={{ color: "#888" }}>Status</span>
                                <span className={`ou-status-pill ${getStatusClass(outletUserDetail?.status)}`}>{outletUserDetail?.status || "N/A"}</span>
                            </div>
                        </div>
                        <div style={{ background: "#f8f8f6", borderRadius: 12, padding: "14px 16px" }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: 1, textTransform: "uppercase", margin: "0 0 10px" }}>Outlet Info</p>
                            {[
                                ["Name", outletUserDetail?.outlet?.name],
                                ["Location", [outletUserDetail?.outlet?.city, outletUserDetail?.outlet?.state].filter(Boolean).join(", ")],
                            ].map(([label, val]) => (
                                <div key={label} className="d-flex justify-content-between mb-2" style={{ fontSize: 12 }}>
                                    <span style={{ color: "#888" }}>{label}</span>
                                    <span style={{ fontWeight: 500 }}>{val || "N/A"}</span>
                                </div>
                            ))}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="ou-btn-secondary" onClick={() => setOpenViewModal(false)}>Close</button>
                    </Modal.Footer>
                </Modal>

                {/* ── DELETE MODAL ── */}
                <Modal dialogClassName="ou-modal" show={openDeleteModal} onHide={() => setOpenDeleteModal(false)} centered size="sm">
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="py-3 px-4" style={{ fontSize: 13 }}>
                        <div style={{ textAlign: "center", padding: "8px 0" }}>
                            <i className="ri-error-warning-line" style={{ fontSize: 40, color: "#dc2626", display: "block", marginBottom: 10 }}></i>
                            Are you sure you want to delete this user? This action <strong>cannot be undone</strong>.
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="ou-btn-secondary" onClick={() => setOpenDeleteModal(false)}>Cancel</button>
                        <button type="button" className="btn btn-danger btn-sm" style={{ borderRadius: 9, fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }} onClick={handleDeleteOutletUser} disabled={isDeleteBtnLoading}>
                            {isDeleteBtnLoading ? <Spinner animation="border" size="sm" /> : "Delete User"}
                        </button>
                    </Modal.Footer>
                </Modal>

            </div>
        </React.Fragment>
    );
};

export default OutletUsers;